import { SPOTCHECK_SYSTEM_FIELDS, SpotcheckStatusFilter } from '@cheqroom/api';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';

import type { FilterFieldEntry } from './types';
import { getDynamicFilterEntries } from './utils/get-dynamic-filter-entries';

const getSpotcheckStatusOptions = (t: TFunction<'spotcheck-filters'>) => [
	{ label: t('spotcheck-filters:status.options.in_progress'), value: SpotcheckStatusFilter.IN_PROGRESS },
	{ label: t('spotcheck-filters:status.options.finished'), value: SpotcheckStatusFilter.FINISHED },
	{
		label: t('spotcheck-filters:status.options.finished_with_issues'),
		value: SpotcheckStatusFilter.FINISHED_WITH_ISSUES,
	},
	{ label: t('spotcheck-filters:status.options.archived'), value: SpotcheckStatusFilter.ARCHIVED },
];

export const getSpotcheckKindOptions = (t: TFunction<'spotcheck-filters'>) => [
	{ label: t('spotcheck-filters:kind.options.order'), value: 'order', icon: 'CollectIn' },
	{ label: t('spotcheck-filters:kind.options.reservation'), value: 'reservation', icon: 'Reservation' },
	{ label: t('spotcheck-filters:kind.options.kit'), value: 'kit', icon: 'Kits' },
	{ label: t('spotcheck-filters:kind.options.custody'), value: 'custody', icon: 'Custody' },
];

interface UseSpotcheckFiltersProps {
	t: TFunction<'spotcheck-filters' | 'ticket-filters' | 'common'>;
}

export const useSpotcheckFilters = ({ t }: UseSpotcheckFiltersProps) => {
	const filters = useMemo<FilterFieldEntry[]>(() => {
		const staticFilters: FilterFieldEntry[] = [
			{
				id: 'status',
				name: t('spotcheck-filters:status.trigger'),
				options: getSpotcheckStatusOptions(t),
				type: 'select',
			},
			{
				id: 'participants',
				name: t('spotcheck-filters:participant.trigger'),
				type: 'user',
				searchPlaceholder: t('spotcheck-filters:participant.search'),
				emptyLabel: t('common:filter.empty.search', { resource: 'participants' }),
			},
			{
				id: 'assignees',
				name: t('ticket-filters:assignee.trigger'),
				type: 'user',
				searchPlaceholder: t('ticket-filters:assignee.search'),
				emptyLabel: t('common:filter.empty.search', { resource: 'assignees' }),
			},
			{
				id: 'kind',
				name: t('spotcheck-filters:kind.trigger'),
				options: getSpotcheckKindOptions(t),
				type: 'select',
			},
			{ id: 'date_range', name: t('common:filter.date.trigger_dates'), type: 'dateRange' },
		];

		const dynamicFilters = getDynamicFilterEntries(SPOTCHECK_SYSTEM_FIELDS);

		return [...staticFilters, ...dynamicFilters];
	}, [t]);

	return {
		filters,
	};
};
